import { useCommercePermissionAccess } from '@/hooks/use-commerce-permission-access';
import { useSalonSetup } from '@/hooks/use-salon-setup';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useOrg } from '@/providers';
import { useUser } from '@/lib/auth';
import { apiClient } from '@/api/axios-instance';
import { useGetLocations } from '@/api/generated/locations/locations';
import { useGetStaff } from '@/api/generated/staff/staff';
import {
  getGetGiftCardsQueryKey,
  getGetGiftCardsIdQueryKey,
} from '@/api/generated/gift-cards/gift-cards';
import { getGetClientsClientIdGiftCardsQueryKey } from '@/api/generated/clients/clients';
import { OperationsViewOnlyBanner, PageCanvas, SplitLayout, SidePanelPlaceholder } from '@/components/layout';
import { CheckoutPanel } from '@/components/calendar/checkout-panel';
import { PaymentPanel } from '@/components/calendar/payment-panel';
import { SaleDetailsPanel } from '@/components/calendar/sale-details-panel';
import { ClientDetailDrawerById } from '@/features/clients/components/client-detail-drawer-by-id';
import { SalesDirectoryToolbar } from '@/features/sales/components/sales-directory-toolbar';
import { SalesList } from '@/features/sales/components/sales-list';
import { SaleDetailSkeleton } from '@/features/sales/components/sale-detail-skeleton';
import { buildSaleItemsPatchPayload, hasSaleItemsPatchChanges } from '@/lib/sale-items-patch';
import { useSaleMutations, saleCompletionInvalidationKeys } from '@/features/sales/checkout';
import { deleteSale as deleteSaleRequest } from '@/features/sales/checkout/sale-mutation-api';
import { UnsavedChangesModal } from '@/components/ui/unsaved-changes-modal';
import type { SaleData, CheckoutDraftPayload } from '@/components/calendar/checkout-panel';
import type { PaymentMethod } from '@/components/calendar/payment-panel';
import { useClientPermissionAccess } from '@/hooks/use-client-permission-access';
import { formatDocumentTitle } from '@/lib/document-title';
import { getApiErrorMessage } from '@/lib/error-utils';
import { useSkeletonUntilFresh } from '@/hooks/use-query-loading-state';

// ============================================
// Types matching API schema
// ============================================

/** Sale list item (no lineItems/payments) */
interface SaleListItem {
  id: string;
  organizationId: string;
  locationId: string | null;
  clientId: string | null;
  appointmentId: string | null;
  saleNumber: number;
  status: 'OPEN' | 'COMPLETED' | 'REFUNDED';
  subtotalCents: number;
  discountCents: number;
  taxCents: number;
  tipCents: number;
  totalCents: number;
  openedAt: string;
  completedAt: string | null;
  notes: string | null;
  createdByName: string | null;
  client: {
    id: string;
    firstName: string;
    lastName: string | null;
    phone: string | null;
  } | null;
  createdAt: string;
  // Group-sale fields projected by the API (recipient names redacted to null when hidden).
  isShared?: boolean;
  appointments?: Array<{
    appointmentId: string | null;
    clientId: string | null;
    clientNameSnapshot: string | null;
  }>;
}

interface SaleListResponse {
  success: boolean;
  data: {
    sales: SaleListItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

interface SaleDetailResponse {
  success: boolean;
  data: SaleData;
}

/** Side panel mode for sales page */
type SalesPanelMode = 'none' | 'detail' | 'checkout' | 'payment';

const salesPanelCardClass =
  'h-full overflow-hidden rounded-[var(--r-2xl)] border border-clovie-border bg-clovie-surface shadow-soft-lg lg:rounded-none lg:border-0 lg:shadow-none';

function SalesPanelCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full bg-clovie-canvas p-3 lg:p-0">
      <div className={salesPanelCardClass}>{children}</div>
    </div>
  );
}

function invalidateGiftCardCachesForSale(
  queryClient: QueryClient,
  sale: SaleData,
  redeemedGiftCardId?: string
) {
  const giftCardLineItems = sale.lineItems.filter((item) => item.itemType === 'GIFT_CARD');
  const shouldRefreshGiftCards = giftCardLineItems.length > 0 || Boolean(redeemedGiftCardId);
  if (!shouldRefreshGiftCards) return;

  const giftCardIds = new Set<string>();
  const clientIds = new Set<string>();
  const saleClientId = sale.client?.id ?? sale.clientId ?? null;

  if (redeemedGiftCardId) giftCardIds.add(redeemedGiftCardId);
  if (saleClientId) clientIds.add(saleClientId);

  for (const item of giftCardLineItems) {
    if (item.giftCardId) giftCardIds.add(item.giftCardId);
    if (item.giftCardOwnerClientId) clientIds.add(item.giftCardOwnerClientId);
  }

  queryClient.invalidateQueries({ queryKey: getGetGiftCardsQueryKey() });
  giftCardIds.forEach((id) => {
    queryClient.invalidateQueries({ queryKey: getGetGiftCardsIdQueryKey(id) });
  });
  clientIds.forEach((clientId) => {
    queryClient.invalidateQueries({ queryKey: getGetClientsClientIdGiftCardsQueryKey(clientId) });
  });
}

// ============================================
// Route definition
// ============================================

export const Route = createFileRoute('/_auth/$orgId/sales')({
  component: SalesPage,
});

function SalesPage() {
  const { scopeKey } = useClientPermissionAccess();
  const access = useCommercePermissionAccess();
  const { shortCode } = useOrg();
  const salonSetup = useSalonSetup();
  // Salons on their own POS (Sales off) have no Sales page; render nothing until the setup is known so
  // the page never flashes before redirecting (`isLoading` alone is false for a paused offline
  // fetch). A settings failure falls back to hiding.
  if (!salonSetup.isResolved && !salonSetup.isError) return null;
  if (!salonSetup.setup.modules.sales) {
    return <Navigate to="/$orgId" params={{ orgId: shortCode }} replace />;
  }
  if (!access.canViewSales) return <p className="p-6 text-clovie-muted">You do not have access to sales.</p>;
  return <ScopedSalesPage key={scopeKey} />;
}

function ScopedSalesPage() {
  const privacy = useClientPermissionAccess();
  const { orgId } = useOrg();
  const commerce = useCommercePermissionAccess();
  const canWriteOperational = commerce.canCheckout;
  const queryClient = useQueryClient();
  const { user } = useUser();
  // All sale writes go through this hook (identity for shared sales, byte-identical bodies for
  // standalone sales, centralized retry/stale handling). See features/sales/checkout.
  const saleMutations = useSaleMutations();

  // Selected sale row in list (for highlighting)
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null);
  const [drawerClientId, setDrawerClientId] = useState<string | null>(null);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);

  // Checkout/payment panel state
  const [checkoutSale, setCheckoutSale] = useState<SaleData | null>(null);
  const [isPaymentView, setIsPaymentView] = useState(false);
  const [paymentSaleNotes, setPaymentSaleNotes] = useState('');
  const [isGoingToPayments, setIsGoingToPayments] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  // Draft subtotal from checkout panel (for optimistic total display in table)
  const [draftSubtotalCents, setDraftSubtotalCents] = useState<number | null>(null);
  // Track "new checkout" sale ID that hasn't been persisted (no "Go to Payments" yet)
  const newCheckoutSaleIdRef = useRef<string | null>(null);
  // Dirty check for unsaved changes modal
  const [hasCheckoutChanges, setHasCheckoutChanges] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  // Bumped on every selection change; an async sale load only applies while its token is current,
  // so a late response cannot re-open a sale the user already left (row switch or mobile Back).
  const selectionTokenRef = useRef(0);

  // Get org timezone from locations
  const { data: locationsData } = useGetLocations();
  const timezone = locationsData?.data?.[0]?.timezone || undefined;

  // Fetch staff list for checkout panel dropdown
  const { data: staffData } = useGetStaff({
    isServiceProvider: 'true',
    isActive: 'true',
    limit: '10',
  });

  const staffList = useMemo(() => {
    const items = staffData?.data?.items ?? [];
    return items.map((s) => ({
      id: s.id,
      firstName: s.firstName,
      lastName: s.lastName,
      avatarUrl: s.avatarUrl,
      canBeAssignedToProductSales: s.canBeAssignedToProductSales,
    }));
  }, [staffData]);

  // Match logged-in user to a staff member for default service assignment
  const defaultStaff = useMemo(() => {
    if (!user || staffList.length === 0) return null;
    const userFirst = (user.firstName ?? '').toLowerCase();
    const userLast = (user.lastName ?? '').toLowerCase();
    const match = staffList.find(
      (s) =>
        s.firstName.toLowerCase() === userFirst && (s.lastName ?? '').toLowerCase() === userLast
    );
    if (!match) return null;
    return { id: match.id, name: `${match.firstName} ${match.lastName ?? ''}`.trim() };
  }, [user, staffList]);

  // Fetch sales list
  const {
    data: salesResponse,
    isLoading,
    error,
  } = useQuery<SaleListResponse>({
    queryKey: ['sales', orgId, privacy.scopeKey],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('limit', '50');
      params.set('sortBy', 'createdAt');
      params.set('sortOrder', 'desc');
      const response = await apiClient.get<SaleListResponse>(`/sales?${params.toString()}`);
      return response.data;
    },
    enabled: !!orgId,
  });

  const sales = salesResponse?.data?.sales ?? [];

  // Shared fetcher for a single sale's full detail. Reused by the detail query,
  // the checkout open path, and the background prefetch so they all hit the same
  // react-query cache (clicking a prefetched sale is then instant).
  const fetchSaleDetail = useCallback(
    (saleId: string) =>
      queryClient.fetchQuery<SaleDetailResponse>({
        queryKey: ['sale-detail', saleId, privacy.scopeKey],
        queryFn: async () => {
          const response = await apiClient.get<SaleDetailResponse>(`/sales/${saleId}`);
          return response.data;
        },
        staleTime: 60_000,
      }),
    [queryClient, privacy.scopeKey]
  );

  // Prefetch a single sale's detail on row hover/focus — only fetches what the
  // user is about to click, so it stays cheap even with many sale records
  // (unlike prefetching the whole list up front). The click then hits cache.
  const prefetchSaleDetail = useCallback(
    (saleId: string) => {
      queryClient.prefetchQuery<SaleDetailResponse>({
        queryKey: ['sale-detail', saleId, privacy.scopeKey],
        queryFn: async () => {
          const response = await apiClient.get<SaleDetailResponse>(`/sales/${saleId}`);
          return response.data;
        },
        staleTime: 60_000,
      });
    },
    [queryClient, privacy.scopeKey]
  );

  // Fetch selected sale details (full data with lineItems + payments) — for non-OPEN sales
  const saleDetailQuery = useQuery<SaleDetailResponse>(
    {
      queryKey: ['sale-detail', selectedSaleId, privacy.scopeKey],
      queryFn: async () => {
        const response = await apiClient.get<SaleDetailResponse>(`/sales/${selectedSaleId}`);
        return response.data;
      },
      // Only fetch detail when we have a selection AND not in checkout/loading-checkout mode
      enabled: !!selectedSaleId && !checkoutSale && !isLoadingCheckout,
      staleTime: 60_000,
    }
  );

  const isLoadingSaleDetail = saleDetailQuery.isLoading;
  // A sale reopened on a stale cache (e.g. refunded/voided since) shows the skeleton until its
  // refetch lands instead of painting the outdated copy; hover-prefetched (fresh) sales stay instant.
  const isSaleDetailRefreshing =
    useSkeletonUntilFresh(saleDetailQuery, selectedSaleId) && !saleDetailQuery.isPending;
  const selectedSaleData = isSaleDetailRefreshing ? null : (saleDetailQuery.data?.data ?? null);

  // Determine panel mode
  const panelMode: SalesPanelMode =
    isPaymentView && checkoutSale
      ? 'payment'
      : checkoutSale
        ? 'checkout'
        : selectedSaleData
          ? 'detail'
          : 'none';

  // Delete unpersisted new checkout sale and refresh list after deletion completes
  const deleteUnpersistedSale = useCallback(
    async (saleId: string) => {
      try {
        await deleteSaleRequest(saleId, null);
      } catch {
        /* ignore */
      }
      queryClient.invalidateQueries({ queryKey: ['sales', orgId] });
    },
    [queryClient, orgId]
  );

  // Switch to a different sale (after dirty check passes)
  const switchToSale = useCallback(
    async (sale: SaleListItem) => {
      // Delete unpersisted new checkout if switching to another sale
      if (newCheckoutSaleIdRef.current && newCheckoutSaleIdRef.current !== sale.id) {
        await deleteUnpersistedSale(newCheckoutSaleIdRef.current);
        newCheckoutSaleIdRef.current = null;
      }

      const token = ++selectionTokenRef.current;
      // Reset any existing checkout state (incl. a superseded load's spinner — its finally is skipped)
      setIsLoadingCheckout(false);
      setCheckoutSale(null);
      setIsPaymentView(false);
      setPaymentSaleNotes('');
      setDraftSubtotalCents(null);
      setHasCheckoutChanges(false);
      setSelectedSaleId(sale.id);

      if (canWriteOperational && sale.status === 'OPEN') {
        setIsLoadingCheckout(true);
        try {
          // Served instantly from cache when prefetched, otherwise fetches.
          const response = await fetchSaleDetail(sale.id);
          if (token === selectionTokenRef.current) setCheckoutSale(response.data);
        } catch (err) {
          if (token === selectionTokenRef.current) toast.error(getApiErrorMessage(err, 'Failed to load sale'));
        } finally {
          if (token === selectionTokenRef.current) setIsLoadingCheckout(false);
        }
      }
    },
    [canWriteOperational, deleteUnpersistedSale, fetchSaleDetail]
  );

  // Handle row click — dirty check before switching
  const handleRowClick = useCallback(
    (sale: SaleListItem) => {
      if (checkoutSale && checkoutSale.id === sale.id) return;

      // If there are unsaved changes on the current checkout, show confirmation
      if (newCheckoutSaleIdRef.current && hasCheckoutChanges) {
        pendingActionRef.current = () => switchToSale(sale);
        setShowDiscardModal(true);
        return;
      }

      switchToSale(sale);
    },
    [checkoutSale, hasCheckoutChanges, switchToSale]
  );

  // Force close checkout — delete unpersisted sale, no dirty check
  const forceCloseCheckout = useCallback(() => {
    if (newCheckoutSaleIdRef.current) {
      deleteUnpersistedSale(newCheckoutSaleIdRef.current);
      newCheckoutSaleIdRef.current = null;
      // The discarded draft no longer exists — drop the selection instead of loading its detail.
      setSelectedSaleId(null);
    }
    setCheckoutSale(null);
    setIsPaymentView(false);
    setPaymentSaleNotes('');
    setDraftSubtotalCents(null);
    setHasCheckoutChanges(false);
  }, [deleteUnpersistedSale]);

  // Close checkout panel — dirty check if new checkout has changes
  const handleCloseCheckout = useCallback(() => {
    if (newCheckoutSaleIdRef.current && hasCheckoutChanges) {
      pendingActionRef.current = () => forceCloseCheckout();
      setShowDiscardModal(true);
      return;
    }
    forceCloseCheckout();
  }, [hasCheckoutChanges, forceCloseCheckout]);

  // Phones/tablets (single-pane): Back returns to the list — closes any checkout (same unsaved-changes
  // guard as Close) and clears the selection.
  const clearSaleSelection = useCallback(() => {
    selectionTokenRef.current += 1;
    forceCloseCheckout();
    setIsLoadingCheckout(false);
    setSelectedSaleId(null);
  }, [forceCloseCheckout]);

  // Back stays disabled while a payment step is in flight (its result re-selects the sale).
  const isMobileBackDisabled = isGoingToPayments || isProcessingPayment;

  const handleMobileBack = useCallback(() => {
    if (isMobileBackDisabled) return;
    if (newCheckoutSaleIdRef.current && hasCheckoutChanges) {
      pendingActionRef.current = clearSaleSelection;
      setShowDiscardModal(true);
      return;
    }
    clearSaleSelection();
  }, [isMobileBackDisabled, hasCheckoutChanges, clearSaleSelection]);

  // Handle discard confirmation from modal
  const handleDiscardConfirm = useCallback(() => {
    setShowDiscardModal(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  }, []);

  // Cleanup unpersisted sale on unmount (e.g. navigating away)
  useEffect(() => {
    return () => {
      if (newCheckoutSaleIdRef.current) {
        deleteSaleRequest(newCheckoutSaleIdRef.current, null).catch(() => {});
      }
    };
  }, []);

  // Handle "New Checkout" button — create a standalone sale via API
  const handleNewCheckout = useCallback(async () => {
    if (isCreatingCheckout) return;
    const locationId = locationsData?.data?.[0]?.id;
    if (!locationId) {
      toast.error('No location found. Please set up a location first.');
      return;
    }

    setIsCreatingCheckout(true);
    try {
      const createdByName = user
        ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'Staff'
        : 'Staff';
      const newSale = await saleMutations.createSale({ locationId, createdByName });

      // Track as unpersisted new checkout (will be deleted if user navigates away)
      newCheckoutSaleIdRef.current = newSale.id;
      selectionTokenRef.current += 1;
      setIsLoadingCheckout(false);

      // Open checkout panel with the new empty sale
      setCheckoutSale(newSale);
      setSelectedSaleId(newSale.id);
      setIsPaymentView(false);
      setPaymentSaleNotes('');

      // Refresh sales list to show the new record
      queryClient.invalidateQueries({ queryKey: ['sales', orgId] });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to create checkout'));
    } finally {
      setIsCreatingCheckout(false);
    }
  }, [isCreatingCheckout, locationsData, user, queryClient, orgId, saleMutations]);

  // Handle client change from checkout panel (update sale's client)
  const handleClientChange = useCallback(
    async (
      client: { id: string; firstName: string; lastName: string; createdAt?: string | null } | null
    ) => {
      if (!checkoutSale) return;
      try {
        const updatedSale = await saleMutations.updateClient(checkoutSale, client?.id ?? null);
        setCheckoutSale(updatedSale);
        // Refresh sales list to show updated client name
        queryClient.invalidateQueries({ queryKey: ['sales', orgId] });
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Failed to update client'));
      }
    },
    [checkoutSale, queryClient, orgId, saleMutations]
  );

  // Handle "GO TO PAYMENTS" from checkout panel — persist draft changes, open payment panel
  const handleGoToPayments = useCallback(
    async (draft: CheckoutDraftPayload) => {
      if (!checkoutSale) return;

      // Mark sale as persisted (user committed to payment)
      newCheckoutSaleIdRef.current = null;

      setIsGoingToPayments(true);
      try {
        // Persist checkout draft (add/update/remove line items)
        const patchPayload = buildSaleItemsPatchPayload(checkoutSale, draft);
        let saleForPayment = checkoutSale;

        if (hasSaleItemsPatchChanges(patchPayload)) {
          saleForPayment = await saleMutations.updateItems(checkoutSale, patchPayload);
        }

        // Open payment panel with updated sale
        setCheckoutSale(saleForPayment);
        setPaymentSaleNotes(draft.notes || saleForPayment.notes || '');
        setIsPaymentView(true);

        // Refresh sales list so totalCents reflects persisted items
        queryClient.invalidateQueries({ queryKey: ['sales', orgId] });
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Failed to save checkout changes'));
      } finally {
        setIsGoingToPayments(false);
      }
    },
    [checkoutSale, queryClient, orgId, saleMutations]
  );

  // Handle back from payment to checkout
  const handleBackToCheckout = useCallback(() => {
    setIsPaymentView(false);
  }, []);

  // Handle payment method selection — supports split/partial payments
  const handlePaymentMethodSelect = useCallback(
    async (method: PaymentMethod, amountCents: number, tipCents: number, giftCardId?: string) => {
      if (!checkoutSale) return;

      setIsProcessingPayment(true);
      try {
        // 1. Process partial payment
        const saleAmountCents = amountCents - tipCents;
        const { sale: updatedSale } = await saleMutations.addPayment(checkoutSale, {
          paymentMethod: method,
          amountCents: saleAmountCents,
          tipCents,
          ...(giftCardId ? { giftCardId } : {}),
        });
        if (giftCardId) {
          invalidateGiftCardCachesForSale(queryClient, updatedSale, giftCardId);
        }

        // 2. Check if fully paid: sum of payment amountCents >= subtotal
        const subtotalCents = updatedSale.lineItems.reduce(
          (sum: number, item: { totalCents: number }) => sum + item.totalCents,
          0
        );
        const paidAmountCents = updatedSale.payments.reduce(
          (sum: number, p: { amountCents: number }) => sum + p.amountCents,
          0
        );

        if (paidAmountCents >= subtotalCents) {
          // Fully paid - complete the sale. Pass the post-payment sale so a grouped sale's
          // completion carries the current version (standalone sales ignore version).
          const { sale: completedSaleData, appointments: completedAppointments } =
            await saleMutations.complete(updatedSale);
          invalidateGiftCardCachesForSale(queryClient, completedSaleData, giftCardId);

          const completedSaleId = checkoutSale.id;
          // The cached detail is the pre-payment snapshot (hover prefetch / checkout open). Drop it
          // so the re-selected sale shows the skeleton and then its completed state, instead of an
          // OPEN sale with no payments for a round-trip. (Invalidating is not enough: the detail
          // query is disabled during checkout, so it would not refetch before the panel paints.)
          queryClient.removeQueries({ queryKey: ['sale-detail', completedSaleId] });
          setCheckoutSale(null);
          setIsPaymentView(false);
          setPaymentSaleNotes('');
          setSelectedSaleId(completedSaleId);

          queryClient.invalidateQueries({ queryKey: ['sales', orgId] });
          // A shared group sale completes EVERY selected member at once (`appointments[]`); refresh
          // each affected appointment's cached sale state so a just-paid member stops offering
          // checkout, plus the calendar/open-group panel (the `['calendar']` key is a prefix of both).
          // A single sale yields just the calendar key — no behaviour change for standalone sales.
          for (const key of saleCompletionInvalidationKeys(completedAppointments, completedSaleData.lineItems)) {
            queryClient.invalidateQueries({ queryKey: key });
          }

          toast.success('Payment completed successfully!');
        } else {
          // Partial payment - update sale state to show new transaction
          setCheckoutSale(updatedSale);
        }
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Failed to process payment'));
      } finally {
        setIsProcessingPayment(false);
      }
    },
    [checkoutSale, queryClient, orgId, saleMutations]
  );

  // Handle sale notes change from payment panel
  const handlePaymentSaleNotesChange = useCallback((notes: string) => {
    setPaymentSaleNotes(notes);
  }, []);

  // Build side panel content based on mode
  let sidePanel: React.ReactNode;
  if (panelMode === 'payment' && checkoutSale) {
    sidePanel = (
      <div className="h-full animate-slide-in-right">
        <PaymentPanel
          sale={checkoutSale}
          saleNotes={paymentSaleNotes}
          onSaleNotesChange={handlePaymentSaleNotesChange}
          onBack={handleBackToCheckout}
          onPaymentMethodSelect={handlePaymentMethodSelect}
          isProcessing={isProcessingPayment}
          timezone={timezone}
        />
      </div>
    );
  } else if (panelMode === 'checkout' && checkoutSale) {
    sidePanel = (
      <CheckoutPanel
        key={checkoutSale.id}
        sale={checkoutSale}
        clientInfo={
          checkoutSale.client
            ? {
                id: checkoutSale.client.id,
                firstName: checkoutSale.client.firstName,
                lastName: checkoutSale.client.lastName ?? '',
                createdAt: checkoutSale.client.createdAt ?? checkoutSale.createdAt,
              }
            : undefined
        }
        onClose={handleCloseCheckout}
        onGoToPayments={handleGoToPayments}
        onCancelCheckout={handleCloseCheckout}
        onClientChange={handleClientChange}
        defaultStaff={defaultStaff}
        onSubtotalChange={setDraftSubtotalCents}
        onHasChangesChange={setHasCheckoutChanges}
        staffList={staffList}
        isLoading={isGoingToPayments}
        timezone={timezone}
      />
    );
  } else if (panelMode === 'detail' && selectedSaleData) {
    sidePanel = (
      <SaleDetailsPanel
        sale={selectedSaleData}
        timezone={timezone}
        onClientClick={(clientId) => setDrawerClientId(clientId)}
      />
    );
  } else if (selectedSaleId && (isLoadingCheckout || isLoadingSaleDetail || isSaleDetailRefreshing)) {
    // A sale was clicked but its detail/checkout data is still loading.
    sidePanel = <SaleDetailSkeleton />;
  } else if (selectedSaleId && saleDetailQuery.isError) {
    sidePanel = (
      <SidePanelPlaceholder title="Couldn't load this sale." description="Please try again." />
    );
  } else {
    sidePanel = (
      <SidePanelPlaceholder
        title="Create a new checkout or select"
        description="an existing sale to view details."
      />
    );
  }

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Sales')}</title>
      </Helmet>
      <SplitLayout
        sidePanel={<SalesPanelCard>{sidePanel}</SalesPanelCard>}
        sidePanelWidth="md"
        mobileDetailOpen={Boolean(selectedSaleId || checkoutSale)}
        onMobileBack={handleMobileBack}
        mobileBackLabel="Sales"
        mobileBackDisabled={isMobileBackDisabled}
      >
        <PageCanvas className="h-full overflow-auto">
          <div className="min-h-full overflow-hidden rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface shadow-soft-lg">
            <OperationsViewOnlyBanner />
            <SalesDirectoryToolbar
              saleCount={sales.length}
              onNewCheckout={handleNewCheckout}
              isCreatingCheckout={isCreatingCheckout}
              readOnly={!canWriteOperational}
            />

            <SalesList
              sales={sales}
              timezone={timezone}
              checkoutSaleId={checkoutSale?.id ?? null}
              draftSubtotalCents={draftSubtotalCents}
              selectedSaleId={selectedSaleId}
              onRowClick={handleRowClick}
              onRowHover={(sale) => prefetchSaleDetail(sale.id)}
              isLoading={isLoading}
              error={error}
            />
          </div>
        </PageCanvas>
      </SplitLayout>

      {/* Unsaved changes confirmation for new checkout */}
      <UnsavedChangesModal
        open={showDiscardModal}
        onClose={() => {
          setShowDiscardModal(false);
          pendingActionRef.current = null;
        }}
        onDiscard={handleDiscardConfirm}
      />

      {/* Client detail drawer — opened from sale detail CLIENT section */}
      <ClientDetailDrawerById
        clientId={drawerClientId}
        onClose={() => setDrawerClientId(null)}
        onClientUpdated={() => void queryClient.invalidateQueries({ queryKey: ['sales'] })}
      />
    </>
  );
}
